import { useState, useEffect } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { rpcCall, subscribe } from "../ws/client";
import { toast } from "../components/ui/Toast";
import "../styles/Referral.scss";

interface ReferralStats {
    total_referrals: number;
    active_referrals: number;
    total_earned: number;
    commission_rate: number;
    my_referrer_code: string | null;
}

interface Payout {
    id: string;
    wallet: string;
    amount: number;
    status: "pending" | "approved" | "rejected" | "completed";
    created_at: string;
    processed_at: string | null;
    tx_hash: string | null;
    rejection_reason: string | null;
}

interface ReferralProgramProps {
    baseUrl?: string;
}

// Telegram bot username (без @)
const TELEGRAM_BOT_USERNAME = "jet_p2p_bot";

export default function ReferralProgram({ baseUrl }: ReferralProgramProps) {
    const wallet = useTonWallet();
    const [referralCode, setReferralCode] = useState<string | null>(null);
    const [referralLink, setReferralLink] = useState<string | null>(null);
    const [stats, setStats] = useState<ReferralStats | null>(null);
    const [payouts, setPayouts] = useState<Payout[]>([]);
    const [copied, setCopied] = useState(false);
    const [payoutAmount, setPayoutAmount] = useState<string>("10");
    const [requesting, setRequesting] = useState(false);

    // Fetch referral link and stats on mount
    useEffect(() => {
        if (wallet?.account?.address) {
            fetchReferralLink();
            fetchReferralStats();
            fetchPayouts();
            
            // Subscribe to referral updates
            const unsubscribeReferral = subscribe("referral.updated", () => {
                fetchReferralStats();
            });
            
            // Subscribe to payout updates
            const unsubscribePayout = subscribe("payout.updated", () => {
                fetchPayouts();
            });
            
            return () => {
                unsubscribeReferral();
                unsubscribePayout();
            };
        }
    }, [wallet]);

    const fetchReferralLink = async () => {
        if (!wallet?.account?.address) return;

        try {
            const response = await rpcCall<
                { wallet: string; baseUrl?: string },
                { code: string; link: string }
            >("referral.get_link", {
                wallet: wallet.account.address,
                baseUrl
            });

            console.log('Referral link response:', response);
            if (response.result) {
                const code = response.result.code;
                setReferralCode(code);
                
                // Генерируем правильную Telegram deep link ссылку
                // Формат: https://t.me/<bot>?start=ref_<code>
                const deepLink = `https://t.me/${TELEGRAM_BOT_USERNAME}?start=ref_${code}`;
                setReferralLink(deepLink);
                
                console.log('Referral deep link:', deepLink);
            }
        } catch (error) {
            console.error('Failed to fetch referral link:', error);
        }
    };

    const fetchReferralStats = async () => {
        if (!wallet?.account?.address) return;

        try {
            const response = await rpcCall<
                { wallet: string },
                ReferralStats
            >("referral.stats", {
                wallet: wallet.account.address
            });

            console.log('Referral stats response:', response);
            if (response.result) {
                setStats(response.result);
            }
        } catch (error) {
            console.error('Failed to fetch referral stats:', error);
        }
    };

    const fetchPayouts = async () => {
        if (!wallet?.account?.address) return;

        try {
            const response = await rpcCall<
                { wallet: string },
                { payouts: Payout[] }
            >("referral.get_payouts", {
                wallet: wallet.account.address
            });

            console.log('Payouts response:', response);
            if (response.result?.payouts) {
                setPayouts(response.result.payouts);
            }
        } catch (error) {
            console.error('Failed to fetch payouts:', error);
        }
    };

    const requestPayout = async () => {
        if (!wallet?.account?.address) return;

        const amount = parseFloat(payoutAmount);
        
        if (isNaN(amount) || amount < 10) {
            toast.error("Минимальная сумма выплаты - 10 TON");
            return;
        }

        if (!stats || amount > stats.total_earned) {
            toast.error("Недостаточно средств для выплаты");
            return;
        }

        setRequesting(true);
        try {
            const response = await rpcCall<
                { wallet: string; amount: number },
                { payout: Payout; success: boolean }
            >("referral.request_payout", {
                wallet: wallet.account.address,
                amount
            });

            console.log('Payout request response:', response);
            if (response.result?.success) {
                toast.success("Заявка на выплату создана!");
                setPayoutAmount("10");
                fetchReferralStats();
                fetchPayouts();
            } else if (response.error) {
                toast.error(response.error.message || "Ошибка создания заявки");
            }
        } catch (error: any) {
            console.error('Failed to request payout:', error);
            toast.error(error?.message || "Не удалось создать заявку");
        } finally {
            setRequesting(false);
        }
    };

    const copyToClipboard = async () => {
        if (!referralLink) {
            toast.error("Ссылка не найдена");
            return;
        }

        try {
            await navigator.clipboard.writeText(referralLink);
            setCopied(true);
            toast.success("Ссылка скопирована!");
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            toast.error("Не удалось скопировать");
        }
    };

    const shareViaTelegram = () => {
        if (!referralLink) return;
        
        const text = `🎁 Присоединяйся к Jet P2P — лучшему P2P обменнику на TON!\n\n` +
            `✅ Безопасные сделки с эскроу\n` +
            `✅ Низкие комиссии 0.5%\n` +
            `✅ Моментальные выплаты\n\n` +
            `Используй мою ссылку для старта:`;
        
        // Используем Telegram share URL с deep link
        const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`;
        window.open(telegramUrl, '_blank');
    };

    if (!wallet) {
        return (
            <div className="referral-program">
                <div className="referral-card">
                    <h3>🎁 Реферальная программа</h3>
                    <p>Подключи кошелёк, чтобы получить уникальную ссылку и зарабатывать 10% с комиссий!</p>
                    <div className="wallet-required">
                        <span className="icon">🔗</span>
                        <span>Требуется подключение кошелька</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="referral-program">
            <div className="referral-card">
                <h3>🎁 Реферальная программа</h3>
                <p className="subtitle">Зарабатывай 10% с каждой сделки твоих друзей!</p>
                
                {/* Stats Section */}
                {stats && (
                    <div className="referral-stats">
                        <div className="stat-item">
                            <span className="stat-value">{stats.total_referrals}</span>
                            <span className="stat-label">Всего приглашено</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-value">{stats.active_referrals}</span>
                            <span className="stat-label">Активных</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-value">{stats.total_earned.toFixed(5)}</span>
                            <span className="stat-label">TON заработано</span>
                        </div>
                    </div>
                )}
                
                {/* Referral Link Section */}
                {referralCode && referralLink ? (
                    <div className="referral-link-section">
                        <label>Твой реферальный код</label>
                        <div className="code-display">
                            <code>{referralCode}</code>
                        </div>
                        
                        <label htmlFor="referral-link-input">Твоя реферальная ссылка</label>
                        <div className="link-display">
                            <input 
                                id="referral-link-input"
                                type="text" 
                                value={referralLink} 
                                readOnly 
                                className="link-input"
                            />
                            <button 
                                onClick={copyToClipboard}
                                className={`copy-btn ${copied ? 'copied' : ''}`}
                            >
                                {copied ? '✓' : '📋'}
                            </button>
                        </div>
                        
                        <div className="share-buttons">
                            <button onClick={copyToClipboard} className="share-btn">
                                📋 Копировать ссылку
                            </button>
                            <button onClick={shareViaTelegram} className="share-btn telegram">
                                ✈️ Поделиться в Telegram
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="loading-section">
                        <button onClick={fetchReferralLink} className="generate-btn">
                            🔗 Получить реферальную ссылку
                        </button>
                    </div>
                )}
                
                {/* Payout Section */}
                {stats && stats.total_earned > 0 && (
                    <div className="payout-section">
                        <h4>💰 Вывод заработанных средств</h4>
                        <div className="payout-balance">
                            <span className="balance-label">Доступно для вывода:</span>
                            <span className="balance-amount">{stats.total_earned.toFixed(5)} TON</span>
                        </div>
                        
                        <div className="payout-form">
                            <div className="input-group">
                                <label htmlFor="payout-amount">Сумма (мин. 10 TON):</label>
                                <input
                                    id="payout-amount"
                                    type="number"
                                    min="10"
                                    step="0.1"
                                    value={payoutAmount}
                                    onChange={(e) => setPayoutAmount(e.target.value)}
                                    className="payout-input"
                                />
                            </div>
                            <button 
                                onClick={requestPayout}
                                disabled={requesting || parseFloat(payoutAmount) < 10 || parseFloat(payoutAmount) > stats.total_earned}
                                className="request-payout-btn"
                            >
                                {requesting ? "⏳ Отправка..." : "💸 Заказать выплату"}
                            </button>
                        </div>

                        {/* Payout History */}
                        {payouts.length > 0 && (
                            <div className="payout-history">
                                <h5>История выплат:</h5>
                                <div className="payouts-list">
                                    {payouts.map(payout => (
                                        <div key={payout.id} className={`payout-item ${payout.status}`}>
                                            <div className="payout-info">
                                                <span className="payout-amount">{payout.amount} TON</span>
                                                <span className="payout-date">
                                                    {new Date(payout.created_at).toLocaleDateString('ru-RU')}
                                                </span>
                                            </div>
                                            <div className="payout-status">
                                                {payout.status === "pending" && "⏳ Ожидает"}
                                                {payout.status === "approved" && "✅ Одобрено"}
                                                {payout.status === "completed" && "✅ Выплачено"}
                                                {payout.status === "rejected" && "❌ Отклонено"}
                                            </div>
                                            {payout.tx_hash && (
                                                <div className="payout-tx">
                                                    TX: <code>{payout.tx_hash.substring(0, 8)}...</code>
                                                </div>
                                            )}
                                            {payout.rejection_reason && (
                                                <div className="payout-rejection">
                                                    Причина: {payout.rejection_reason}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Info Section */}
                <div className="referral-info">
                    <h4>Как это работает:</h4>
                    <ul>
                        <li>Поделись уникальной ссылкой с друзьями</li>
                        <li>Они подключат кошелёк и начнут торговать</li>
                        <li>Ты получаешь 10% от их комиссий</li>
                        <li>Начисления автоматические в TON</li>
                    </ul>
                </div>
                
                {/* My Referrer */}
                {stats?.my_referrer_code && (
                    <div className="my-referrer">
                        <span>Тебя пригласил: </span>
                        <code>{stats.my_referrer_code}</code>
                    </div>
                )}
            </div>
        </div>
    );
}
